import {
  ApiError,
  assignUserToLocation,
  listAssignedUsers,
  unassignUserFromLocation,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { AssignedUserResponse } from '@jq4race/shared';
import { Button, Card, CardContent } from '@jq4race/ui';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, Plus, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { RestrictedMeta } from '../../../../shared/errors/RestrictedMeta';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAutoDismissFeedback } from '../../../../shared/hooks/useAutoDismissFeedback';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { AssignedUsersTable } from '../components/AssignedUsersTable';
import { AssignUserModal } from '../components/AssignUserModal';
import { RemoveAssignmentDialog } from '../components/RemoveAssignmentDialog';

const ASSIGNED_LIMIT = 20;

export function LocationAssignmentsPage() {
  const { t } = useTranslation('location-assignments');
  // Read locationId from URL params (sub-page pattern matching zones).
  const { locationId } = useParams({ strict: false });

  // ── State ────────────────────────────────────────────────────────────────
  const [assignedUsers, setAssignedUsers] = useState<AssignedUserResponse[]>([]);
  const [assignedTotal, setAssignedTotal] = useState(0);
  const [assignedPage, setAssignedPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // See EventsPage.tsx for the confirmed production incident this guards
  // against: a 403 (role changed mid-session) must not render like "no
  // users assigned", and its banner must not auto-dismiss.
  const [accessDenied, setAccessDenied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<AssignedUserResponse | null>(null);
  const [assigning, setAssigning] = useState(false);

  // ── Data fetching ────────────────────────────────────────────────────────

  const reload = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const res = await listAssignedUsers(locationId, {
        page: assignedPage,
        limit: ASSIGNED_LIMIT,
      });
      setAssignedUsers(res.items);
      setAssignedTotal(res.total);
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('feedback.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, assignedPage, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoDismissFeedback({
    error,
    success,
    isErrorPersistent: accessDenied,
    onDismissError: () => setError(null),
    onDismissSuccess: () => setSuccess(null),
  });

  // ── Assign handler ───────────────────────────────────────────────────────

  async function handleAssign(userId: string) {
    if (!locationId) return;
    setAssigning(true);
    try {
      const { created } = await assignUserToLocation(locationId, { userId });
      setSuccess(created ? t('feedback.assignSuccess') : t('feedback.alreadyAssigned'));
      setShowAssignModal(false);
      setAssignedPage(1);
      void reload();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(translateApiError(e, t, { fallback: t('feedback.assignError') }));
      } else {
        setError(translateApiError(e, t, { fallback: t('feedback.assignError') }));
      }
    } finally {
      setAssigning(false);
    }
  }

  // ── Remove handler ───────────────────────────────────────────────────────

  async function handleRemoveConfirm() {
    if (!locationId || !removeTarget) return;
    try {
      await unassignUserFromLocation(locationId, removeTarget.id);
      setSuccess(t('feedback.removeSuccess', { email: removeTarget.email }));
      setAssignedPage(1);
      void reload();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(translateApiError(e, t, { fallback: t('feedback.removeError') }));
      } else {
        setError(translateApiError(e, t, { fallback: t('feedback.removeError') }));
      }
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const alreadyAssignedIds = new Set(assignedUsers.map((u) => u.id));

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
        meta={
          <p className="text-[13px] text-jq-graphite">
            <RestrictedMeta
              loading={loading && assignedUsers.length === 0}
              accessDenied={accessDenied}
            >
              <span className="font-medium tabular-nums text-jq-charcoal">{assignedTotal}</span>{' '}
              {t('unit', { count: assignedTotal })}
            </RestrictedMeta>
          </p>
        }
        actions={
          <Button size="sm" onClick={() => setShowAssignModal(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.75} />
            {t('page.addUser')}
          </Button>
        }
      />

      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {accessDenied ? (
            <AccessDeniedState />
          ) : (
            <AssignedUsersTable
              users={assignedUsers}
              loading={loading}
              onRemove={setRemoveTarget}
            />
          )}
          {!accessDenied && assignedTotal > ASSIGNED_LIMIT && (
            <div className="flex items-center justify-between pt-4 text-[13px]">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAssignedPage((p) => p - 1)}
                disabled={assignedPage === 1}
              >
                {t('pagination.previous')}
              </Button>
              <span className="text-jq-graphite">
                {t('pagination.pageOfTotal', { page: assignedPage, total: assignedTotal })}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAssignedPage((p) => p + 1)}
                disabled={assignedPage * ASSIGNED_LIMIT >= assignedTotal}
              >
                {t('pagination.next')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AssignUserModal
        open={showAssignModal}
        alreadyAssignedIds={alreadyAssignedIds}
        submitting={assigning}
        onAssign={handleAssign}
        onClose={() => setShowAssignModal(false)}
      />

      <RemoveAssignmentDialog
        user={removeTarget}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}

// ── Feedback banner ──────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('location-assignments');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
